/**
 * Each locale resolves to its own chunk via vite manualChunks
 * (`i18n-<locale>-<hash>.js`); the loader returned by import.meta.glob
 * fires on demand only.
 */
import { reloadOnce } from "@/lib/chunkReload";

const LOADERS = import.meta.glob<Record<string, unknown>>(
  "../../i18n/messages/*.json",
);

export type Messages = Record<string, unknown>;

/** Locales bundled in /src/i18n/messages/*.json. */
export const SUPPORTED_LOCALES: string[] = Object.keys(LOADERS).map((path) => {
  const m = path.match(/\/([^/]+)\.json$/);
  return m![1]!;
});

export const DEFAULT_LOCALE = "en-GB";

// Aliases that match the legacy `@/i18n/config` API surface so bulk-ported
// callers (`import { locales, Locale } from "@/i18n/config"`) keep working
// without code changes. `defaultLocale` mirrors legacy too.
export const locales = SUPPORTED_LOCALES;
export type Locale = string;
export const defaultLocale: Locale = DEFAULT_LOCALE;

/**
 * The bundled locale closest to `locale`: itself, else the first of its
 * language ("en" → "en-…"), else DEFAULT_LOCALE. For a value that must name a
 * locale the backend renders mails in (W4 arrival-notice contacts).
 */
export function closestSupportedLocale(locale: string): string {
  if (SUPPORTED_LOCALES.includes(locale)) return locale;
  const lang = locale.split("-")[0];
  return SUPPORTED_LOCALES.find((code) => code.startsWith(`${lang}-`)) ?? DEFAULT_LOCALE;
}

export function pickInitialLocale(): string {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem("veltric.locale");
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  }
  if (typeof navigator !== "undefined") {
    const nav = navigator.language;
    if (SUPPORTED_LOCALES.includes(nav)) return nav;
    // fall through: try language match (en-GB → en-US fallback)
    const lang = nav.split("-")[0];
    const match = SUPPORTED_LOCALES.find((l) => l.startsWith(`${lang}-`));
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

export function deepMerge(base: Messages, over: Messages): Messages {
  const out: Messages = { ...base };
  for (const k of Object.keys(over)) {
    const a = (out as Record<string, unknown>)[k];
    const b = (over as Record<string, unknown>)[k];
    if (
      a && b &&
      typeof a === "object" && typeof b === "object" &&
      !Array.isArray(a) && !Array.isArray(b)
    ) {
      (out as Record<string, unknown>)[k] = deepMerge(a as Messages, b as Messages);
    } else {
      (out as Record<string, unknown>)[k] = b;
    }
  }
  return out;
}

/**
 * Await a locale chunk and unwrap it, surviving a stale deploy. After a
 * Cloudflare Pages deploy, public/_redirects serves index.html with a 200
 * for a deleted `i18n-<locale>-<hash>.js`, so the import RESOLVES to a
 * junk/undefined namespace instead of rejecting — `mod.default` then
 * throws a raw TypeError that chunkReload never sees. Retry once
 * (transient blip / mid-deploy race), else return null so callers can
 * fall back + trigger the bounded reload.
 */
const RETRY_DELAY_MS = 250;

async function loadBundle(
  loader: () => Promise<Record<string, unknown>>,
): Promise<Messages | null> {
  const unwrap = (mod: ({ default?: Messages } & Messages) | undefined) =>
    mod ? ((mod.default ?? mod) as Messages) : null;
  try {
    const msgs = unwrap((await loader()) as { default?: Messages } & Messages);
    if (msgs) return msgs;
  } catch {
    // fall through to the single retry
  }
  await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
  try {
    return unwrap((await loader()) as { default?: Messages } & Messages);
  } catch {
    return null;
  }
}

/** Load the en-GB fallback floor (~563 KB). */
export async function loadDefaultFloor(): Promise<Messages> {
  const defaultLoader = LOADERS[`../../i18n/messages/${DEFAULT_LOCALE}.json`];
  if (!defaultLoader) {
    throw new Error(`Default locale ${DEFAULT_LOCALE} bundle missing`);
  }
  const msgs = await loadBundle(defaultLoader);
  if (msgs) return msgs;
  // No floor to fall back to — reload into the new bundle. Reached-after-
  // reloadOnce only when the reload was suppressed (cap spent / dev):
  // message matches isStaleChunkError so error paths behave as today.
  reloadOnce();
  throw new Error(
    `Failed to fetch dynamically imported module: stale chunk (i18n ${DEFAULT_LOCALE})`,
  );
}

/**
 * Load ONLY the requested locale's bundle (no fallback floor, no merge). Used
 * for first paint: the active locale is rendered immediately and the en-GB
 * floor is merged in afterwards in the background (see I18nProvider). An
 * unknown locale falls back to the floor so callers always get messages.
 */
export async function loadLocaleMessages(locale: string): Promise<Messages> {
  const loader = LOADERS[`../../i18n/messages/${locale}.json`];
  if (!loader) return loadDefaultFloor();
  const msgs = await loadBundle(loader);
  if (msgs) return msgs;
  // Stale chunk: keep the UI translated via the floor while the bounded
  // reload brings in the new bundle.
  reloadOnce();
  return loadDefaultFloor();
}

/**
 * Full bundle: active locale merged over the en-GB floor. Kept for callers that
 * need the complete map in one await. The first-paint path in I18nProvider
 * instead uses loadLocaleMessages + a background floor merge so the en-GB load
 * (563–607 KB) no longer gates first paint.
 */
export async function loadMessages(locale: string): Promise<Messages> {
  const defaultMsgs = await loadDefaultFloor();
  if (locale === DEFAULT_LOCALE) return defaultMsgs;
  const target = `../../i18n/messages/${locale}.json`;
  const loader = LOADERS[target];
  if (!loader) return defaultMsgs;
  const userMsgs = await loadBundle(loader);
  if (!userMsgs) {
    // Stale chunk: the floor alone is still a complete Messages map.
    reloadOnce();
    return defaultMsgs;
  }
  return deepMerge(defaultMsgs, userMsgs);
}
